import { useEffect, useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DollarSign, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { useAuth } from './auth/ParsleeAuthProvider';
import { PlatformTopBarWrapper } from './components/PlatformTopBarWrapper';
import { EmployeeStatusCard } from './components/EmployeeStatusCard';
import { TokenUsageCard } from './components/TokenUsageCard';
import { HealthStatusCard } from './components/HealthStatusCard';
import { BusinessMetricsCard } from './components/BusinessMetricsCard';
import { dashboardApi } from './services/dashboardApi';
import type { DashboardSummaryResponse } from './models/types';
import './styles/App.css';

const DASHBOARD_REFRESH_INTERVAL_MS = 30_000; // 30 seconds
const DEFAULT_ORGANIZATION_ID = 'org_button_up';

function App() {
  // Auth comes from the session (U2). ProtectedRoute + StaffGate guarantee an
  // authenticated Parslee staffer by the time this renders. Identity/org/logout
  // for the top bar are owned by PlatformTopBarWrapper (U4); this component only
  // needs the active org id to scope the dashboard fetch.
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active org comes from the session (replaces the old Teams-SDK / MSAL derivation).
  const organizationId = user?.organizationId || DEFAULT_ORGANIZATION_ID;

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await dashboardApi.getDashboardSummary(organizationId);
        setDashboardData(data);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const message = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown error';
          setError(`API Error (${err.response?.status || 'Network'}): ${message}`);
        } else {
          setError(err instanceof Error ? err.message : 'Unknown error occurred');
        }
        console.error('[Dashboard] Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
    const intervalId = setInterval(fetchDashboard, DASHBOARD_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [organizationId]);

  // Render the shared platform top bar (U4) above the dashboard content. The
  // top bar owns its own styling and reads identity/org from the session itself.
  const renderWithTopBar = (content: React.ReactNode) => (
    <>
      <PlatformTopBarWrapper />
      <div style={{ paddingTop: 'var(--topbar-height)', minHeight: '100vh' }}>
        {content}
      </div>
    </>
  );

  if (loading) {
    return renderWithTopBar(
      <div className="loading-container">
        <Spinner size={32} label="Loading dashboard..." />
      </div>
    );
  }

  if (error) {
    return renderWithTopBar(
      <div className="error-container">
        <h1 className="text-2xl font-bold text-foreground">Error</h1>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!dashboardData) {
    return renderWithTopBar(
      <div className="error-container">
        <p className="text-sm">No dashboard data available</p>
      </div>
    );
  }

  return renderWithTopBar(
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1 className="text-2xl font-bold text-foreground">AI Employee Dashboard</h1>
        </div>
        <div className="dashboard-header-actions">
          <a href="/admin" className={cn(buttonVariants({ variant: 'ghost' }))}>
            <ShieldCheck className="h-4 w-4" />
            Admin
          </a>
          <a href="/billing" className={cn(buttonVariants({ variant: 'ghost' }))}>
            <DollarSign className="h-4 w-4" />
            Billing
          </a>
        </div>
      </div>
      <p className="last-updated text-sm">
        Last updated: {new Date(dashboardData.generatedAt).toLocaleString()}
      </p>

      <div className="dashboard-grid">
        <EmployeeStatusCard employees={dashboardData.employees} />
        <TokenUsageCard tokenUsage={dashboardData.tokenUsage} />
        <HealthStatusCard healthStatus={dashboardData.healthStatus} />
        <BusinessMetricsCard businessMetrics={dashboardData.businessMetrics} />
      </div>
    </div>
  );
}

export default App;
